import { useMemo } from 'react'

import { AttributeGroup, FullTrace, ServiceGroup } from '@ui/interfaces'

interface UseMemoizedDataProps {
  traceServiceGroupQueryData: {
    traceServiceGroups: ServiceGroup[]
  }
  spansByServiceQueryData: {
    spansByService: { record: object; duration: number }[]
  }
  fullTraceQueryData: {
    fullTrace: FullTrace
  }
  spanAttributesQueryData: {
    spanAttributes: AttributeGroup[]
  }
  tracesWithAttributeValueQueryData: {
    tracesWithAttributeValue: { record: object; duration: number }[]
  }
}

export const useTraceExplorerData = ({
  traceServiceGroupQueryData,
  spansByServiceQueryData,
  fullTraceQueryData,
  spanAttributesQueryData,
  tracesWithAttributeValueQueryData,
}: UseMemoizedDataProps) => {
  const serviceGroupData = useMemo(() => {
    if (traceServiceGroupQueryData?.traceServiceGroups) {
      return traceServiceGroupQueryData.traceServiceGroups.map((serviceGroup) => ({
        'Service Name': serviceGroup.serviceName,
        Name: serviceGroup.name,
        'Total Size': serviceGroup.totalSize,
        Count: serviceGroup.count,
      }))
    }
    return []
  }, [traceServiceGroupQueryData])

  const rootTraceData = useMemo(() => {
    if (spansByServiceQueryData?.spansByService) {
      return spansByServiceQueryData.spansByService.map(({ record }) => record)
    }
    return []
  }, [spansByServiceQueryData])

  const fullTraceData = useMemo(() => {
    if (fullTraceQueryData?.fullTrace) {
      return {
        traces: fullTraceQueryData.fullTrace.traces,
        totalDuration: fullTraceQueryData.fullTrace.totalDuration,
      }
    }
    return null
  }, [fullTraceQueryData])

  const attributeGroupData = useMemo(() => {
    if (spanAttributesQueryData?.spanAttributes) {
      return spanAttributesQueryData.spanAttributes.map((attributeGroup) => ({
        name: attributeGroup.name,
        values: attributeGroup.values,
      }))
    }
    return []
  }, [spanAttributesQueryData])

  const tracesWithAttributeValueData = useMemo(() => {
    if (tracesWithAttributeValueQueryData?.tracesWithAttributeValue) {
      return tracesWithAttributeValueQueryData.tracesWithAttributeValue.map(({ record }) => record)
    }
    return []
  }, [tracesWithAttributeValueQueryData])

  return { serviceGroupData, rootTraceData, fullTraceData, attributeGroupData, tracesWithAttributeValueData }
}
